import { FileTranscript, NoteTranslation } from '@kalos/kalos-rpc';
import { useQueries, useQuery } from '@tanstack/react-query';

import { FileTranscriptClientService, NoteTranslationClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

/**
 * Fetches translation for a text note (memo description).
 * Used in MemoDetailSheet.
 */
export const useTextNoteTranslationQuery = ({
  memoId,
  targetLanguage = 'en',
  enabled = true,
}: {
  memoId?: number;
  targetLanguage?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.noteTranslation.root,
      queryKeys.noteTranslation.getTextNoteHash({ memoId: memoId!, targetLanguage }),
    ],
    queryFn: async () => {
      const req = NoteTranslation.create();
      req.memoId = memoId!;
      req.sourceType = 'TEXT_NOTE';
      req.targetLanguage = targetLanguage;
      const res = await NoteTranslationClientService.BatchGet(req);
      return res.results.length > 0 ? res.results[0] : null;
    },
    enabled: enabled && !!memoId,
  });
};

/**
 * Fetches translations for transcript (audio files).
 * Each query: FileTranscript.Get for fileId → NoteTranslation.BatchGet for fileTranscriptId.
 * Returns combined result as Record<fileId, NoteTranslation[]>.
 */
export const useTranscriptTranslationsQueries = ({
  fileIds,
  targetLanguage = 'en',
  enabled = true,
}: {
  fileIds: number[];
  targetLanguage?: string;
  enabled?: boolean;
}) => {
  const queries = useQueries({
    queries: fileIds.map((fileId) => ({
      queryKey: [
        queryKeys.noteTranslation.root,
        queryKeys.noteTranslation.getByFileIdHash({ fileId, targetLanguage }),
      ],
      queryFn: async (): Promise<{ fileId: number; translations: NoteTranslation[] }> => {
        const ftReq = FileTranscript.create();
        ftReq.fileId = fileId;
        const ft = await FileTranscriptClientService.Get(ftReq);
        if (!ft?.fileTranscriptId) return { fileId, translations: [] };

        const translationReq = NoteTranslation.create();
        translationReq.fileTranscriptId = ft.fileTranscriptId;
        translationReq.sourceType = 'TRANSCRIPT';
        translationReq.targetLanguage = targetLanguage;
        const res = await NoteTranslationClientService.BatchGet(translationReq);
        return { fileId, translations: res.results ?? [] };
      },
      enabled: enabled && !!fileId,
    })),
  });

  const translationsByFileId = queries.reduce(
    (acc, q) => {
      const data = q.data;
      if (data && data.translations.length > 0) {
        acc[data.fileId] = data.translations;
      }
      return acc;
    },
    {} as Record<number, NoteTranslation[]>,
  );

  const isPending = queries.some((q) => q.isPending);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error;

  return {
    data: translationsByFileId,
    translationsByFileId,
    queries,
    isPending,
    isError,
    error,
  };
};
